import React, { useState, useEffect } from 'react';
import { Edit, Save, X } from 'lucide-react';
import axios from 'axios';
import { BASE_URL } from '../../../config/api';
import { message } from 'antd';
import { useParams } from 'react-router-dom';
import { set } from 'date-fns';

const highlightYaml = (code) => {
  if (!code) return '';
  
  return code.split('\n').map((line) => {
    // Highlight keys
    line = line.replace(/^(\s*)([^:\s]+):/g, '$1<span class="text-indigo-600">$2</span>:');
    
    // Highlight values
    line = line.replace(/:\s*([^#\s][^#]*?)$/g, ': <span class="text-green-600">$1</span>');
    
    // Highlight array items
    line = line.replace(/^(\s*)-\s+(.+)$/g, '$1- <span class="text-green-600">$2</span>');
    
    // Highlight version
    line = line.replace(/^version:\s*(\d+)/g, '<span class="text-indigo-600">version</span>: <span class="text-green-600">$1</span>');
    
    return line;
  }).join('\n');
};

const BuildSpec = ({ frontendApp }) => {
  const { projectId } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [buildSpec, setBuildSpec] = useState('');
  const [selectedFramework, setSelectedFramework] = useState('');
  const [showEditButton, setShowEditButton] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const amplifyAppId = frontendApp?.amplifyAppId;
  const amplifyAppName = frontendApp?.appName;

  const frameworkSpecs = {
    react: `version: 1
frontend:
  phases:
    preBuild:
      commands:
        - npm ci --cache .npm --prefer-offline
    build:
      commands:
        - npm run build
  artifacts:
    baseDirectory: build
    files:
      - '**/*'
  cache:
    paths:
      - .npm/**/*`,
    next: `version: 1
frontend:
  phases:
    preBuild:
      commands:
        - yarn install --immutable
    build:
      commands:
        - yarn run build
  artifacts:
    baseDirectory: .next
    files:
      - '**/*'
  cache:
    paths:
      - .next/cache/**/*
      - node_modules/**/*`,
    vue: `version: 1
frontend:
  phases:
    preBuild:
      commands:
        - npm ci
    build:
      commands:
        - npm run build
  artifacts:
    baseDirectory: dist
    files:
      - '**/*'
  cache:
    paths:
      - node_modules/**/*`
  };

  useEffect(() => {
    fetchAmplifyBuildSpec();
  }, [amplifyAppId]);

  
  const fetchAmplifyBuildSpec = async () => {
    
    try {
      setLoading(true);
      if(frontendApp?.status === 'Active' && amplifyAppId) {
        const response = await axios.get(`https://cloudify-prod.wizr.in/api/amplify/apps/${amplifyAppId}/build-spec`);
        if (response.data.buildSpec) {
          setBuildSpec(response.data.buildSpec);
        }
      }else if(frontendApp?.appId){
        const response = await axios.get(`https://cloudify-prod.wizr.in/${projectId}/frontend-app/${frontendApp?.appId}/build-spec`);
        setBuildSpec(response.data.buildSpec);
        setSelectedFramework(response.data.framework);

      }
    } catch (error) {
      console.error('Error fetching build specification:', error);
      message.error('Failed to fetch build specification');
    } finally {
      setLoading(false);
    }
  };

  const handleFrameworkSelect = async (framework) => {
    setSelectedFramework(framework);
    const newBuildSpec = frameworkSpecs[framework];
    setBuildSpec(newBuildSpec);
  };

  

  const handleSave = async () => {
    try {
      setLoading(true);
      
      if(frontendApp?.status === 'Active' && amplifyAppId) {
        await axios.put(`https://cloudify-prod.wizr.in/api/amplify/apps/${amplifyAppId}/build-spec`,
          {
            buildSpec: buildSpec
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );;
      }

      await axios.put(`https://cloudify-prod.wizr.in/${projectId}/frontend-app/${frontendApp?.appId}/build-spec`, 
        {
          framework: selectedFramework,
          buildSpec: buildSpec,
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      
      setIsEditing(false);
      message.success('Build specification updated successfully');
    } catch (error) {
      console.error('Error updating build specification:', error);
      message.error('Failed to update build specification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Build Specification</h3>
            <p className="mt-1 text-sm text-gray-500">
              Configure how your application should be built and deployed
            </p>
          </div>
          <div className="flex items-center space-x-4">
            
            <select
              value={selectedFramework}
              onChange={(e) => handleFrameworkSelect(e.target.value)}
              className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            
            >
              <option value="">Select Framework</option>
              <option value="react">React</option>
              <option value="next">Next.js</option>
              <option value="vue">Vue.js</option>
            </select>
          </div>
        </div>
      </div>

      <div className="px-4 py-5 sm:p-6">
        {loading ? (
          <div className="fixed inset-0 flex items-center justify-center bg-white">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : (selectedFramework || buildSpec) ? (
          <div 
            className="relative bg-gray-50 rounded-md border border-gray-200"
            onMouseEnter={() => !isEditing && setShowEditButton(true)}
            onMouseLeave={() => !isEditing && setShowEditButton(false)}
          >
            {isEditing ? (
              <textarea
                value={buildSpec}
                onChange={(e) => setBuildSpec(e.target.value)}
                className="w-full h-[32rem] font-mono text-sm p-4 bg-white rounded-md border-0 focus:ring-2 focus:ring-indigo-500"
                spellCheck="false"
              />
            ) : (
              <>
                <pre 
                  className="w-full h-[32rem] font-mono text-sm p-4 overflow-auto whitespace-pre"
                  dangerouslySetInnerHTML={{ __html: highlightYaml(buildSpec) }}
                />
                {showEditButton && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2"
                  >
                    <Edit className="h-5 w-5 text-gray-600" />
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            {!amplifyAppId? (
              <p className="text-sm text-gray-500">
                No Amplify app configured. Please provide an app ID or name.
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                Select a framework to view and edit its build specification
              </p>
            )}
          </div>
        )}

        {isEditing && (
          <div className="flex justify-end pt-4 gap-2">
            <button
              onClick={() => {
                setIsEditing(false);
                setBuildSpec(frontendApp?.buildSpec || frameworkSpecs[selectedFramework] || '');
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors mr-2"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuildSpec;